/**
 * Color Scheme Hook for A/B Testing
 *
 * Returns color classes based on PostHog feature flag "landing_color_scheme"
 * Variant: "blue" (default). The "purple" variant was retired (off-brand:
 * reserved purple + forbidden pink); legacy 'purple' flag values fall back to blue.
 */

import { useState, useEffect } from 'react';
import { getFeatureFlag, trackEvent } from '../lib/posthog';

// Color class mappings
// NOTE: 'emerald' is the official /tmp Labs brand color scheme
// 'blue' is the landing-page default; the 'purple' A/B variant was retired
const COLOR_SCHEMES = {
  // Official /tmp Labs Brand Colors - Primary: #10B981 (Emerald), Secondary: #0D9488 (Teal)
  emerald: {
    // Primary colors
    primary: 'bg-emerald-500',
    primaryHover: 'hover:bg-emerald-600',
    primaryText: 'text-emerald-600',
    primaryTextHover: 'hover:text-emerald-700',

    // Secondary/lighter colors
    secondary: 'bg-teal-600',
    secondaryText: 'text-teal-600',

    // Light backgrounds
    lightBg: 'bg-emerald-50',
    lightBg100: 'bg-emerald-100',
    lightBg200: 'bg-emerald-200',

    // Gradients (brand gradient: emerald to teal)
    gradient: 'bg-gradient-to-r from-emerald-500 to-teal-600',
    gradientHeader: 'bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-600',
    gradientText: 'bg-gradient-to-r from-emerald-500 to-teal-500',

    // Borders and rings
    border: 'border-emerald-200',
    borderHover: 'hover:border-emerald-300',
    ring: 'focus:ring-emerald-500',

    // Text variants
    text100: 'text-emerald-100',
    text200: 'text-emerald-200',
    text400: 'text-emerald-400',
    text500: 'text-emerald-500',
    text700: 'text-emerald-700',
    text800: 'text-emerald-800',

    // Shadows
    shadow: 'shadow-emerald-500/25',

    // Avatar gradient
    avatarGradient: 'bg-gradient-to-br from-emerald-500 to-teal-500',

    // Badge
    badge: 'bg-emerald-100 text-emerald-700',
    badgeDot: 'bg-emerald-500',

    // Scheme identifier
    scheme: 'emerald'
  },
  blue: {
    // Primary colors
    primary: 'bg-blue-700',
    primaryHover: 'hover:bg-blue-800',
    primaryText: 'text-blue-700',
    primaryTextHover: 'hover:text-blue-700',

    // Secondary/lighter colors
    secondary: 'bg-blue-600',
    secondaryText: 'text-blue-600',

    // Light backgrounds
    lightBg: 'bg-blue-50',
    lightBg100: 'bg-blue-100',
    lightBg200: 'bg-blue-200',

    // Gradients
    gradient: 'bg-gradient-to-r from-blue-700 to-blue-900',
    gradientHeader: 'bg-gradient-to-r from-blue-700 via-blue-800 to-blue-900',
    gradientText: 'bg-gradient-to-r from-blue-600 to-blue-400',

    // Borders and rings
    border: 'border-blue-200',
    borderHover: 'hover:border-blue-300',
    ring: 'focus:ring-blue-500',

    // Text variants
    text100: 'text-blue-100',
    text200: 'text-blue-200',
    text400: 'text-blue-400',
    text500: 'text-blue-500',
    text700: 'text-blue-700',
    text800: 'text-blue-800',

    // Shadows
    shadow: 'shadow-blue-500/25',

    // Avatar gradient
    avatarGradient: 'bg-gradient-to-br from-blue-600 to-blue-400',

    // Badge
    badge: 'bg-blue-100 text-blue-700',
    badgeDot: 'bg-blue-500',

    // Scheme identifier
    scheme: 'blue'
  }
};

export const useColorScheme = () => {
  const [scheme, setScheme] = useState('blue');
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Check PostHog feature flag after a short delay to allow flags to load
    const checkFeatureFlag = () => {
      const flagValue = getFeatureFlag('landing_color_scheme', 'blue');
      // Purple variant retired (off-brand: reserved purple + forbidden pink).
      // Any unknown/legacy flag value (e.g. 'purple') falls back to blue.
      const validScheme = COLOR_SCHEMES[flagValue] ? flagValue : 'blue';
      setScheme(validScheme);
      setIsLoaded(true);

      // Track which variant the user saw
      trackEvent('color_scheme_viewed', { scheme: validScheme });
    };

    // Check immediately and again after flags potentially load
    checkFeatureFlag();
    const timeout = setTimeout(checkFeatureFlag, 1000);

    return () => clearTimeout(timeout);
  }, []);

  return {
    colors: COLOR_SCHEMES[scheme],
    scheme,
    isLoaded,
    // Helper to track conversions
    trackConversion: (action) => {
      trackEvent('landing_conversion', {
        scheme,
        action,
        timestamp: new Date().toISOString()
      });
    }
  };
};

// Export color schemes for direct use if needed
export { COLOR_SCHEMES };

export default useColorScheme;
